import React, { useState, useCallback, useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import { AppRoute, SnackbarType, UserRole } from './types/enums';
import { Loader, Snackbar } from './components';
import {
  ContactUsUser,
  DashboardAdded,
  Dashboards,
  EditDashboard,
  EditUser,
  EditWebsite,
  ForgotPassword,
  Login,
  NewDashboard,
  NewWebsite,
  Onboarding,
  Profile,
  Register,
  ResetPassword,
  Upgrade,
  Users,
  WebsiteAdded,
  Websites
} from './pages';
import { axiosInstance } from './requests/axios';
import { deleteToken, loginByToken } from './utils/auth';
import { Dashboard, Notify, StateAction, User, Website, WebsiteStatus } from './types/types';
import { fetchDashboards, fetchStaticData, fetchWebsites } from './requests/requests';

import 'react-tooltip/dist/react-tooltip.css';
import './App.scss';

export const UserContext = React.createContext<{
  user: User | undefined;
  setUser: StateAction<User | undefined>;
}>({ user: undefined, setUser: () => {} });

export const DataContext = React.createContext<{
  dashboards: Dashboard[];
  websites: Website[];
  staticData: WebsiteStatus;
  refreshDashboards: () => Promise<Dashboard[]>;
  refreshWebsites: () => Promise<Website[]>;
}>({
  dashboards: [],
  websites: [],
  staticData: { lastCheckedDateTimeUtc: '', monitors: [], displayIconUrl: '', displayTitle: '' },
  refreshDashboards: () => Promise.resolve([]),
  refreshWebsites: () => Promise.resolve([])
});

export const NotificationContext = React.createContext<Notify>(() => {});

function App() {
  const [user, setUser] = useState<User>();
  const [isAuthLoaded, setIsAuthLoaded] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [notification, setNotification] = useState<{
    message: string;
    type: SnackbarType;
  }>();
  const [websites, setWebsites] = useState<Website[]>([]);
  const [dashboards, setDashboards] = useState<Dashboard[]>([]);
  const [staticData, setStaticData] = useState<WebsiteStatus>({
    lastCheckedDateTimeUtc: '',
    monitors: [],
    displayIconUrl: '',
    displayTitle: ''
  });

  const notify = useCallback((message: string, type: SnackbarType) => {
    setNotification({ message, type });

    setTimeout(() => {
      setNotification(undefined);
    }, 8000);
  }, []);

  const refreshStaticData = useCallback(() => {
    if (user?.id) {
      fetchStaticData(user.id)
        .then((data) => {
          if (data) {
            setStaticData(data);
          }
        })
        .catch(() => {
          console.error('Error while fetching static data!');
        });
    }
  }, [user]);

  const refreshDashboards = useCallback(async () => {
    const { data } = await fetchDashboards();
    setDashboards(data.dashboards);

    return data.dashboards;
  }, []);

  const refreshWebsites = useCallback(async () => {
    const { data } = await fetchWebsites();
    setWebsites(data.websites);

    return data.websites;
  }, []);

  const purgeData = useCallback(() => {
    setDashboards([]);
    setWebsites([]);
    setStaticData({
      lastCheckedDateTimeUtc: '',
      monitors: [],
      displayIconUrl: '',
      displayTitle: ''
    });
  }, []);

  useEffect(() => {
    if (user) {
      const timeout = setTimeout(() => {
        refreshStaticData();
      }, 1500);
      const timer = setInterval(refreshStaticData, 10 * 1000);

      return () => {
        clearInterval(timer);
        clearTimeout(timeout);
      };
    } else {
      purgeData();
    }
  }, [user, refreshStaticData, purgeData]);

  useEffect(() => {
    loginByToken()
      .then((userData) => {
        if (userData) {
          setUser(userData);
        }
      })
      .finally(() => {
        setIsAuthLoaded(true);
      });

    axiosInstance.interceptors.request.use((config) => {
      setShowLoader(true);

      return config;
    });
    axiosInstance.interceptors.response.use(
      (response) => {
        setShowLoader(false);

        return response;
      },
      (err) => {
        setShowLoader(false);

        if (err.response.status === 401) {
          deleteToken();
          setUser(undefined);
          return Promise.reject(err);
        }

        notify('Unexpected error, please reach out to us!', SnackbarType.ERROR);

        return Promise.reject(err);
      }
    );
  }, []);

  return (
    <div className="app">
      <UserContext.Provider value={{ user, setUser }}>
        <NotificationContext.Provider value={notify}>
          <BrowserRouter>
            {isAuthLoaded ? (
              user ? (
                <DataContext.Provider
                  value={{
                    websites,
                    dashboards,
                    staticData,
                    refreshDashboards,
                    refreshWebsites
                  }}>
                  <Routes>
                    <>
                      <Route path={AppRoute.WEBSITES} element={<Websites />} />
                      <Route path={AppRoute.DASHBOARDS} element={<Dashboards />} />
                      <Route path={AppRoute.PROFILE} element={<Profile />} />
                      <Route path={AppRoute.UPGRADE} element={<Upgrade />} />
                      <Route path={AppRoute.DASHBOARD_NEW} element={<NewDashboard />} />
                      <Route path={AppRoute.DASHBOARD_EDIT} element={<EditDashboard />} />
                      <Route path={AppRoute.DASHBOARD_CREATED} element={<DashboardAdded />} />
                      <Route path={AppRoute.WEBSITE_NEW} element={<NewWebsite />} />
                      <Route path={AppRoute.WEBSITE_CREATED} element={<WebsiteAdded />} />
                      <Route path={AppRoute.WEBSITE_EDIT} element={<EditWebsite />} />
                      <Route path={AppRoute.CONTACT_US} element={<ContactUsUser />} />
                      {user.role === UserRole.ADMIN ? (
                        <>
                          <Route path={AppRoute.USERS} element={<Users />} />
                          <Route path={AppRoute.USER_EDIT} element={<EditUser />} />
                        </>
                      ) : null}

                      {user.newlyRegistered ? (
                        <>
                          <Route path={AppRoute.ONBOARDING} element={<Onboarding />} />
                          <Route path="*" element={<Navigate to={AppRoute.ONBOARDING} replace />} />
                        </>
                      ) : (
                        <Route path="*" element={<Navigate to={AppRoute.WEBSITES} replace />} />
                      )}
                    </>
                  </Routes>
                </DataContext.Provider>
              ) : (
                <Routes>
                  <>
                    <Route path={AppRoute.REGISTER} element={<Register />} />
                    <Route path={AppRoute.LOGIN} element={<Login />} />
                    <Route path={AppRoute.FORGOT_PASSWORD} element={<ForgotPassword />} />
                    <Route path={AppRoute.PASSWORD_RESET} element={<ResetPassword />} />
                    <Route path="*" element={<Navigate to={AppRoute.LOGIN} replace />} />
                  </>
                </Routes>
              )
            ) : (
              <Loader />
            )}
          </BrowserRouter>
        </NotificationContext.Provider>
      </UserContext.Provider>

      {notification && <Snackbar type={notification.type}>{notification.message}</Snackbar>}

      {showLoader && (
        <div className="loader-wrapper">
          <span className="loader" />
        </div>
      )}
    </div>
  );
}

export default App;
